import useMobileSchedule from '../../../hooks/useMobileSchedule';
import React, { useState, useRef, useEffect, useCallback, useMemo, useContext, createContext } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
    FiHome, FiTrash2, FiCalendar, FiSearch, FiX,
    FiChevronDown, FiFilter, FiEyeOff,
} from 'react-icons/fi';
import { GiWoodBeam } from 'react-icons/gi';
import { BrickWall, Grid2X2, ArrowDownUp, ArrowUpNarrowWide, ArrowDownNarrowWide, Flame, ArrowDownAZ, ArrowUpAZ, GripVertical } from 'lucide-react';
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { updateProductionScheduleCell, removeFromProductionSchedule, updateProductionScheduleSortOrder, updateProductionScheduleVariantOrder, getProductionSortKey } from '../../../lib/supabase/production-schedule';
import { toggleWaitingJob, toggleSentProduction } from '../../../lib/supabase/assignments';
import AssignmentContextMenu from './AssignmentContextMenu';
import Toast from '../../common/Toast';
import { useAuth } from '../../../contexts/AuthContext';
import { hasPermission } from '../../../lib/permissions';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const QUOTE_COLORS = {
    roof: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500', icon: <FiHome className="w-3 h-3" /> },
    wall: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500', icon: <BrickWall className="w-3 h-3" /> },
    floor: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-500', icon: <Grid2X2 className="w-3 h-3" /> },
    timber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500', icon: <GiWoodBeam className="w-3 h-3" /> },
};

// Maps job_production.phase values → display label + colour
const PHASE_COLORS = {
    not_started: 'bg-gray-100 text-gray-600',
    in_cutting: 'bg-yellow-100 text-yellow-700',
    cutting_ended: 'bg-orange-100 text-orange-700',
    in_production: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
};

const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Active cell context (Excel-style navigation) ─────────────────────────────
// activeCell: { rowIdx, colIdx } | null
// EDITABLE_COLS order must match the column order in the table: A B C D E F Date
const EDITABLE_COL_IDS = ['colA', 'colB', 'colC', 'colD', 'colE', 'colF', 'scheduledDate'];

const ActiveCellCtx = createContext({
    activeCell: null,
    setActiveCell: () => { },
    totalRows: 0,
    canEdit: false,
});

// ─── Inline editable text cell ────────────────────────────────────────────────
const EditableCell = ({ value, onSave, placeholder = '', hoverBg = 'hover:bg-gray-50', rowIdx, colIdx }) => {
    const { activeCell, setActiveCell, totalRows, canEdit } = useContext(ActiveCellCtx);
    const isActive = canEdit && activeCell?.rowIdx === rowIdx && activeCell?.colIdx === colIdx;

    const [draft, setDraft] = useState(value || '');
    const inputRef = useRef(null);

    // Sync external value changes
    useEffect(() => { setDraft(value || ''); }, [value]);

    // Auto-focus when this cell becomes active
    useEffect(() => {
        if (isActive && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isActive]);

    const commit = useCallback(async (nextCell) => {
        const trimmed = draft.trim();
        if (trimmed !== (value || '').trim()) {
            try {
                await onSave(trimmed);
            } catch {
                setDraft(value || '');
                Toast.error('Failed to save', { position: 'bottom-right' });
            }
        }
        if (nextCell !== undefined) {
            setActiveCell(nextCell);
        } else {
            setActiveCell(null);
        }
    }, [draft, value, onSave, setActiveCell]);

    const handleKey = useCallback((e) => {
        const totalCols = EDITABLE_COL_IDS.length;
        if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(value || '');
            setActiveCell(null);
        } else if (e.key === 'Enter' || e.key === 'ArrowDown') {
            e.preventDefault();
            const nextRow = rowIdx + 1 < totalRows ? rowIdx + 1 : rowIdx;
            commit({ rowIdx: nextRow, colIdx });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prevRow = rowIdx - 1 >= 0 ? rowIdx - 1 : 0;
            commit({ rowIdx: prevRow, colIdx });
        } else if (e.key === 'Tab') {
            e.preventDefault();
            if (e.shiftKey) {
                // Shift+Tab: move left, wrap up a row
                if (colIdx > 0) {
                    commit({ rowIdx, colIdx: colIdx - 1 });
                } else if (rowIdx > 0) {
                    commit({ rowIdx: rowIdx - 1, colIdx: totalCols - 1 });
                } else {
                    commit(null);
                }
            } else {
                // Tab: move right, wrap down a row
                if (colIdx < totalCols - 1) {
                    commit({ rowIdx, colIdx: colIdx + 1 });
                } else if (rowIdx + 1 < totalRows) {
                    commit({ rowIdx: rowIdx + 1, colIdx: 0 });
                } else {
                    commit(null);
                }
            }
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            // Only navigate if at start/end of text
            const input = inputRef.current;
            const atStart = input?.selectionStart === 0 && input?.selectionEnd === 0;
            const atEnd = input?.selectionStart === input?.value.length;
            if (e.key === 'ArrowLeft' && atStart) {
                e.preventDefault();
                if (colIdx > 0) commit({ rowIdx, colIdx: colIdx - 1 });
            } else if (e.key === 'ArrowRight' && atEnd) {
                e.preventDefault();
                if (colIdx < totalCols - 1) commit({ rowIdx, colIdx: colIdx + 1 });
            }
        }
    }, [rowIdx, colIdx, totalRows, value, commit, setActiveCell]);

    if (isActive) {
        return (
            <input
                ref={inputRef}
                value={draft}
                onChange={e => setDraft(e.target.value)}
                onBlur={() => commit(null)}
                onKeyDown={handleKey}
                className="w-full h-full px-1.5 text-xs border-0 outline-none bg-white ring-2 ring-inset ring-blue-500 rounded-sm"
                style={{ minHeight: 28 }}
            />
        );
    }

    if (!canEdit) {
        return (
            <div className="w-full h-full px-1.5 text-xs text-gray-800 min-h-[28px] flex items-center">
                {draft || <span className="text-gray-300 text-[10px]">{placeholder}</span>}
            </div>
        );
    }

    return (
        <div
            onClick={() => setActiveCell({ rowIdx, colIdx })}
            onFocus={() => setActiveCell({ rowIdx, colIdx })}
            tabIndex={0}
            className={`w-full h-full px-1.5 text-xs text-gray-800 cursor-default select-none ${hoverBg} rounded-sm min-h-[28px] flex items-center outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400`}
            onKeyDown={(e) => {
                // Start editing on printable key press
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
                    setDraft('');
                    setActiveCell({ rowIdx, colIdx });
                } else if (e.key === 'Enter' || e.key === 'F2') {
                    setActiveCell({ rowIdx, colIdx });
                } else if (e.key === 'Delete' || e.key === 'Backspace') {
                    setDraft('');
                    onSave('');
                } else if (e.key === 'Tab') {
                    e.preventDefault();
                    const totalCols = EDITABLE_COL_IDS.length;
                    if (e.shiftKey) {
                        if (colIdx > 0) setActiveCell({ rowIdx, colIdx: colIdx - 1 });
                        else if (rowIdx > 0) setActiveCell({ rowIdx: rowIdx - 1, colIdx: totalCols - 1 });
                    } else {
                        if (colIdx < totalCols - 1) setActiveCell({ rowIdx, colIdx: colIdx + 1 });
                        else if (rowIdx + 1 < totalRows) setActiveCell({ rowIdx: rowIdx + 1, colIdx: 0 });
                    }
                } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    if (colIdx < EDITABLE_COL_IDS.length - 1) setActiveCell({ rowIdx, colIdx: colIdx + 1 });
                } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    if (colIdx > 0) setActiveCell({ rowIdx, colIdx: colIdx - 1 });
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (rowIdx + 1 < totalRows) setActiveCell({ rowIdx: rowIdx + 1, colIdx });
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (rowIdx - 1 >= 0) setActiveCell({ rowIdx: rowIdx - 1, colIdx });
                }
            }}
        >
            {draft || <span className="text-gray-300 text-[10px]">{placeholder}</span>}
        </div>
    );
};

// ─── Inline date cell ─────────────────────────────────────────────────────────
const DateCell = ({ value, onSave, rowIdx, colIdx, quoteDate }) => {
    const { activeCell, setActiveCell, totalRows, canEdit } = useContext(ActiveCellCtx);
    const isActive = canEdit && activeCell?.rowIdx === rowIdx && activeCell?.colIdx === colIdx;
    const inputRef = useRef(null);
    const totalCols = EDITABLE_COL_IDS.length;
    const dateColIdx = EDITABLE_COL_IDS.indexOf('scheduledDate');

    useEffect(() => {
        if (isActive && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.showPicker?.();
        }
    }, [isActive]);

    const handleChange = async (e) => {
        const val = e.target.value || null;
        try { await onSave(val); }
        catch { Toast.error('Failed to save date', { position: 'bottom-right' }); }
        setActiveCell(null);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); setActiveCell(null); }
        else if (e.key === 'Tab') {
            e.preventDefault();
            if (e.shiftKey) {
                if (dateColIdx > 0) setActiveCell({ rowIdx, colIdx: dateColIdx - 1 });
                else if (rowIdx > 0) setActiveCell({ rowIdx: rowIdx - 1, colIdx: totalCols - 1 });
            } else {
                if (rowIdx + 1 < totalRows) setActiveCell({ rowIdx: rowIdx + 1, colIdx: 0 });
                else setActiveCell(null);
            }
        }
    };

    if (isActive) {
        return (
            <input
                ref={inputRef}
                type="date"
                defaultValue={value || quoteDate}
                onChange={handleChange}
                onBlur={() => setActiveCell(null)}
                onKeyDown={handleKeyDown}
                className="w-full h-full px-1.5 text-xs border-0 outline-none bg-white ring-2 ring-inset ring-blue-500 rounded-sm text-end"
                style={{ minHeight: 28 }}
            />
        );
    }

    if (!canEdit) {
        return (
            <div className="w-full h-full px-1.5 text-xs min-h-[28px] flex items-center gap-1 text-gray-800">
                {value ? (
                    <>
                        <FiCalendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                        <span>{formatDate(value)}</span>
                    </>
                ) : quoteDate ?
                    (
                        <div className='flex flex-col'>
                            <div className='flex flex-row'>
                                <FiCalendar className="w-3 h-3 text-gray-400 flex-shrink-0 mr-1 self-center" />
                                <span>{formatDate(quoteDate)}</span>
                            </div>
                            <small className='text-zinc-500'>From Dep. Date</small>
                        </div>
                    )
                    : (
                        <span className="text-gray-300 text-[10px]">—</span>
                    )}
            </div>
        );
    }

    return (
        <div
            onClick={() => setActiveCell({ rowIdx, colIdx: dateColIdx })}
            onFocus={() => setActiveCell({ rowIdx, colIdx: dateColIdx })}
            tabIndex={0}
            className="w-full h-full px-1.5 text-xs cursor-default select-none hover:bg-gray-50 rounded-sm min-h-[28px] flex items-center gap-1 text-gray-800 outline-none focus:ring-2 focus:ring-inset focus:ring-blue-400"
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === 'F2' || e.key === ' ') {
                    e.preventDefault();
                    setActiveCell({ rowIdx, colIdx: dateColIdx });
                } else if (e.key === 'Tab') {
                    e.preventDefault();
                    if (e.shiftKey) {
                        if (dateColIdx > 0) setActiveCell({ rowIdx, colIdx: dateColIdx - 1 });
                        else if (rowIdx > 0) setActiveCell({ rowIdx: rowIdx - 1, colIdx: totalCols - 1 });
                    } else {
                        if (rowIdx + 1 < totalRows) setActiveCell({ rowIdx: rowIdx + 1, colIdx: 0 });
                        else setActiveCell(null);
                    }
                } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    if (dateColIdx > 0) setActiveCell({ rowIdx, colIdx: dateColIdx - 1 });
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    if (rowIdx + 1 < totalRows) setActiveCell({ rowIdx: rowIdx + 1, colIdx: dateColIdx });
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    if (rowIdx - 1 >= 0) setActiveCell({ rowIdx: rowIdx - 1, colIdx: dateColIdx });
                }
            }}
        >
            {value ? (
                <>
                    <FiCalendar className="w-3 h-3 text-gray-400 flex-shrink-0" />
                    <span>{formatDate(value)}</span>
                </>
            ) : quoteDate ?
                (
                    <div className='flex flex-col'>
                        <div className='flex flex-row'>
                            <FiCalendar className="w-3 h-3 text-gray-400 flex-shrink-0 mr-1 self-center" />
                            <span>{formatDate(quoteDate)}</span>
                        </div>
                        <small className='text-zinc-500'>From Dep. Date</small>
                    </div>
                )
                : (
                    <span className="text-gray-300 text-[10px]">Set date</span>
                )}
        </div>
    );
};

// ─── Quote type chip ──────────────────────────────────────────────────────────
const QuoteChip = ({ quoteType }) => {
    const c = QUOTE_COLORS[quoteType] || QUOTE_COLORS.roof;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${c.bg} ${c.text} ${c.border}`}>
            {c.icon}
            {quoteType}
        </span>
    );
};

// ─── Filter toolbar ───────────────────────────────────────────────────────────
const FilterBar = ({
    searchQuery, setSearchQuery,
    quoteTypeFilter, setQuoteTypeFilter,
    sortBy, setSortBy,
    jobTypeFilter, setJobTypeFilter,
    urgentFilter, setUrgentFilter,
    hideCompleted, setHideCompleted,
    totalRows,
}) => {
    const [showFilters, setShowFilters] = useState(false);
    const dropRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setShowFilters(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const SORT_OPTIONS = [
        { value: 'sort_order', label: 'Table Order', icon: <ArrowDownUp className="w-3 h-3" /> },
        { value: 'newest', label: 'Newest First', icon: <ArrowDownNarrowWide className="w-3 h-3" /> },
        { value: 'oldest', label: 'Oldest First', icon: <ArrowUpNarrowWide className="w-3 h-3" /> },
        { value: 'alphabetical', label: 'A → Z', icon: <ArrowDownAZ className="w-3 h-3" /> },
        { value: 'reverse_alphabetical', label: 'Z → A', icon: <ArrowUpAZ className="w-3 h-3" /> },
    ];

    // Timber removed
    const QUOTE_BUTTONS = [
        { key: 'roof', label: 'Roof', icon: <FiHome className="w-3 h-3" /> },
        { key: 'wall', label: 'Wall', icon: <BrickWall className="w-3 h-3" /> },
        { key: 'floor', label: 'Floor', icon: <Grid2X2 className="w-3 h-3" /> },
    ];

    const activeFilterCount = [
        quoteTypeFilter ? 1 : 0,
        jobTypeFilter !== 'all' ? 1 : 0,
        urgentFilter !== 'all' ? 1 : 0,
        hideCompleted ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    return (
        <div className="production-toolbar flex flex-wrap items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
            {/* Search */}
            <div className="relative min-w-[200px] max-w-xs">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search job id or customer…"
                    className="w-full h-8 pl-8 pr-8 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <FiX className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            {/* Quote type pill-buttons (no timber) */}
            <div className="flex items-center gap-1">
                {QUOTE_BUTTONS.map(q => {
                    const active = quoteTypeFilter === q.key;
                    return (
                        <button
                            key={q.key}
                            onClick={() => setQuoteTypeFilter(active ? null : q.key)}
                            className={`flex items-center gap-1 h-8 px-2.5 rounded-md text-xs font-medium border transition-all ${active
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                                }`}
                        >
                            {q.icon}
                            <span className="hidden sm:inline">{q.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Hide completed quick-toggle */}
            <button
                onClick={() => setHideCompleted(v => !v)}
                className={`flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs font-medium border transition-all ${hideCompleted
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400 hover:text-indigo-600'
                    }`}
                title="Hide completed rows"
            >
                <FiEyeOff className="w-3 h-3" />
                <span className="hidden sm:inline">Hide completed</span>
            </button>

            {/* Filters dropdown */}
            <div className="relative" ref={dropRef}>
                <button
                    onClick={() => setShowFilters(v => !v)}
                    className={`flex items-center gap-1.5 h-8 px-3 text-xs font-medium border rounded-md transition-all ${activeFilterCount > 0 || showFilters
                        ? 'bg-indigo-50 border-indigo-400 text-indigo-700'
                        : 'bg-white border-gray-300 text-gray-600 hover:border-indigo-400'
                        }`}
                >
                    <FiFilter className="w-3 h-3" />
                    Filters
                    {activeFilterCount > 0 && (
                        <span className="ml-1 bg-indigo-600 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                            {activeFilterCount}
                        </span>
                    )}
                    <FiChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                </button>

                {showFilters && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-3 space-y-3">
                        {/* Sort */}
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Sort</p>
                            <div className="space-y-1">
                                {SORT_OPTIONS.map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setSortBy(opt.value)}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-all ${sortBy === opt.value
                                            ? 'bg-indigo-50 text-indigo-700 font-medium'
                                            : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {opt.icon} {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Job type */}
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Job Type</p>
                            <div className="space-y-1">
                                {[
                                    { value: 'all', label: 'All' },
                                    { value: 'design_only', label: 'Design Only' },
                                    { value: 'design_estimation_only', label: 'Design & Estimation' },
                                    { value: 'timber_only', label: 'Timber' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setJobTypeFilter(opt.value)}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-all ${jobTypeFilter === opt.value
                                            ? 'bg-indigo-50 text-indigo-700 font-medium'
                                            : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Urgent */}
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Priority</p>
                            <div className="space-y-1">
                                {[
                                    { value: 'all', label: 'All' },
                                    { value: 'urgent_quote', label: 'Urgent Quote' },
                                    { value: 'urgent_design', label: 'Urgent Design' },
                                ].map(opt => (
                                    <button
                                        key={opt.value}
                                        onClick={() => setUrgentFilter(opt.value)}
                                        className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-all ${urgentFilter === opt.value
                                            ? 'bg-indigo-50 text-indigo-700 font-medium'
                                            : 'text-gray-600 hover:bg-gray-50'
                                            }`}
                                    >
                                        {opt.value !== 'all' && <Flame className="w-3 h-3 text-orange-500" />}
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Clear */}
                        {activeFilterCount > 0 && (
                            <button
                                onClick={() => {
                                    setQuoteTypeFilter(null);
                                    setJobTypeFilter('all');
                                    setUrgentFilter('all');
                                    setHideCompleted(false);
                                    setShowFilters(false);
                                }}
                                className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-md border border-red-200 font-medium"
                            >
                                Clear all filters
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Row count */}
            <span className="ml-auto text-xs text-gray-400">{totalRows} row{totalRows !== 1 ? 's' : ''}</span>
        </div>
    );
};

// ─── Sortable table row (dnd-kit) ────────────────────────────────────────────
const SortableRow = ({ rowId, row, r, canDragSort, assignment, ctxMenuProps, virtualIndex, measureElement }) => {
    const { setNodeRef, transform, transition, isDragging, attributes, listeners } = useSortable({
        id: rowId,
        disabled: !canDragSort,
    });

    const rowRef = useCallback(node => {
        setNodeRef(node);
        measureElement(node);
    }, [setNodeRef, measureElement]);

    return (
        <AssignmentContextMenu
            assignment={assignment}
            page="production"
            {...ctxMenuProps}
        >
            <tr
                ref={rowRef}
                data-index={virtualIndex}
                style={{
                    height: 48,
                    opacity: isDragging ? 0 : 1,
                    transform: CSS.Transform.toString(transform),
                    transition,
                }}
                className={`border-b border-gray-400 transition-colors group cursor-context-menu ${r.phase === 'completed' ? 'bg-green-50/40' : 'hover:bg-gray-50'}`}
            >
                {canDragSort && (
                    <td
                        {...attributes}
                        {...listeners}
                        style={{ width: 28 }}
                        className="px-1 align-middle border-r border-gray-400 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 select-none"
                        onClick={e => e.stopPropagation()}
                    >
                        <GripVertical className="w-3.5 h-3.5 mx-auto" />
                    </td>
                )}
                {row.getVisibleCells().map(cell => {
                    const meta = cell.column.columnDef.meta || {};
                    return (
                        <td
                            key={cell.id}
                            style={{ width: cell.column.getSize() }}
                            className={`px-2 py-1 align-middle border-r border-gray-400 last:border-r-0 ${meta.colBg || ''}`}
                        >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    );
                })}
            </tr>
        </AssignmentContextMenu>
    );
};

// ─── Main ProductionTable component ──────────────────────────────────────────
const ProductionTable = ({
    rows,
    onRowsChange,
    isLoading,
    isDragOver,
    setIsDragOver,
    onDrop,
    // context menu handlers from parent
    onOpenAssignment,
    onStartProduction,
    onEndProduction,
    onForceFinish,
    availableDesigners,
    userRole,
    // filter props passed from parent
    searchQuery, setSearchQuery,
    quoteTypeFilter, setQuoteTypeFilter,
    sortBy, setSortBy,
    jobTypeFilter, setJobTypeFilter,
    urgentFilter, setUrgentFilter,
    hideCompleted, setHideCompleted,
}) => {
    const { userProfile, company } = useAuth();
    const canEdit = hasPermission(userProfile?.role, ["app_owner", "admin", "super_admin"]);

    const hasActiveTableFilter =
        !!quoteTypeFilter ||
        jobTypeFilter !== 'all' ||
        urgentFilter !== 'all' ||
        hideCompleted;

    const hasSearch =
        !!searchQuery?.trim();

    const isMobile = useMobileSchedule();
    const canDragSort =
        !isMobile && canEdit &&
        sortBy === 'sort_order' &&
        !hasSearch;
    const tableContainerRef = useRef(null);
    const hasScrolledToBottomRef = useRef(false);

    // ── drag-and-drop sort state ───────────────────────────────────────────────
    const [displayIds, setDisplayIds] = useState([]);
    const [activeRowId, setActiveRowId] = useState(null);
    const reorderLockRef = useRef(false);
    const isDraggingRef = useRef(false);

    const handleToggleWaiting = async (assignmentId, quoteType, currentStatus, quoteId = null) => {
        try {
            await toggleWaitingJob(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            Toast.success(currentStatus ? 'Waiting removed' : 'Marked as Waiting');
        } catch (error) {
            console.error('Error toggling waiting job:', error);
            Toast.error('Failed to update Waiting status');
        }
    };

    const handleToggleSentProduction = async (assignmentId, quoteType, currentStatus, quoteId = null) => {
        try {
            await toggleSentProduction(assignmentId, quoteType, currentStatus, userProfile, quoteId);
            Toast.success(currentStatus ? 'Removed from Production' : 'Sent to Production');
        } catch (error) {
            console.error('Error toggling sent_production:', error);
            Toast.error('Failed to update Production status');
        }
    };

    // ── active cell state (Excel navigation) ──────────────────────────────────
    const [activeCell, setActiveCell] = useState(null); // { rowIdx, colIdx }

    // Click outside table → deactivate
    useEffect(() => {
        const handler = (e) => {
            if (tableContainerRef.current && !tableContainerRef.current.contains(e.target)) {
                setActiveCell(null);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // ── cell-level save helper ─────────────────────────────────────────────────
    const handleCellSave = useCallback(async (rowId, dbColumn, value) => {
        try {
            await updateProductionScheduleCell(rowId, dbColumn, value);
            onRowsChange(prev =>
                prev.map(r => {
                    if (r.id !== rowId) return r;
                    const fieldMap = {
                        col_a: 'colA', col_b: 'colB', col_c: 'colC',
                        col_d: 'colD', col_e: 'colE', col_f: 'colF',
                        scheduled_date: 'scheduledDate',
                        delivery_time: 'deliveryTime',
                    };
                    return { ...r, [fieldMap[dbColumn]]: value };
                })
            );
        } catch {
            Toast.error('Failed to update cell', { position: 'bottom-right' });
        }
    }, [onRowsChange]);

    const handleRemoveRow = useCallback(async (rowId) => {
        try {
            await removeFromProductionSchedule(rowId);
            onRowsChange(prev => prev.filter(r => r.id !== rowId));
            Toast.success('Removed from production schedule', { position: 'bottom-right' });
        } catch {
            Toast.error('Failed to remove row', { position: 'bottom-right' });
        }
    }, [onRowsChange]);

    // ── client-side filtering (search + hide completed) ────────────────────────
    const filteredRows = useMemo(() => {
        let result = rows;
        if (hideCompleted) {
            result = result.filter(r => r.phase !== 'completed');
        }
        if (searchQuery?.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(r =>
                r.uniqueId?.toLowerCase().includes(q) ||
                r.childUniqueId?.toLowerCase().includes(q) ||
                r.customerName?.toLowerCase().includes(q)
            );
        }
        return result;
    }, [rows, searchQuery, hideCompleted]);

    // Sync displayIds from filteredRows (respecting sort_order) when not locked
    useEffect(() => {
        if (reorderLockRef.current) return;
        const sorted = sortBy === 'sort_order'
            ? [...filteredRows].sort((a, b) => {

                const filterKey = getProductionSortKey({
                    quoteTypeFilter,
                    jobTypeFilter,
                    urgentFilter,
                    hideCompleted,
                });


                const aVariant = a.sortOrderVariants?.[filterKey];
                const bVariant = b.sortOrderVariants?.[filterKey];


                if (
                    aVariant !== undefined &&
                    bVariant !== undefined
                ) {
                    return aVariant - bVariant;
                }


                return (a.sortOrder || 0) - (b.sortOrder || 0);

            })
            : filteredRows;
        setDisplayIds(sorted.map(r => r.id));
    }, [filteredRows,
        sortBy,
        quoteTypeFilter,
        jobTypeFilter,
        urgentFilter,
        hideCompleted]);

    // displayRows: filteredRows reordered to match displayIds
    const displayRows = useMemo(() => {
        if (displayIds.length === 0) return filteredRows;
        const byId = Object.fromEntries(filteredRows.map(r => [r.id, r]));
        return displayIds.filter(id => byId[id]).map(id => byId[id]);
    }, [filteredRows, displayIds]);

    // ── columns definition ────────────────────────────────────────────────────
    const columns = useMemo(() => [
        {
            id: 'rowNum',
            header: '#',
            header: () => (
                <div className='flex items-center justify-center'>
                    <span className="items-center self-center">#</span>
                </div>
            ),
            size: 30,
            cell: ({ row }) => (
                <div className='flex'>
                    <span className="text-[11px] text-gray-400 font-mono text-center items-center justify-center content-center self-center mx-auto">{row.index + 1}</span>
                </div>
            ),
        },
        {
            id: 'jobId',
            header: 'Job ID',
            size: 170,
            cell: ({ row }) => {
                const r = row.original;
                const isChild = !!r.childUniqueId;
                return (
                    <div className="flex flex-col gap-0.5">
                        <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-gray-900">
                                {isChild ? r.childUniqueId : r.uniqueId}
                            </span>
                            {isChild && (
                                <span className="text-[10px] text-gray-400 font-normal">
                                    (PRN: {r.uniqueId})
                                </span>
                            )}
                            {(r.urgentQuote || r.urgentDesign) && (
                                <span className="px-1 py-0.5 bg-red-100 text-red-700 text-[9px] font-bold rounded">URG</span>
                            )}
                            <QuoteChip quoteType={r.quoteType} />
                        </div>
                    </div>
                );
            },
        },
        {
            id: 'customer',
            header: 'Customer',
            size: 140,
            cell: ({ row }) => (
                <span className="text-xs text-gray-700 truncate block" title={row.original.customerName}>
                    {row.original.customerName || '—'}
                </span>
            ),
        },
        {
            id: 'productionStatus',
            header: () => <span className="block text-center">Production Status</span>,
            size: 150,
            cell: ({ row }) => {
                // Use phase from job_production row, not mainStatus from jobs
                const phase = row.original.phase || 'not_started';
                const cls = PHASE_COLORS[phase] || 'bg-gray-100 text-gray-600';
                return (
                    <div className="flex justify-center">
                        <span className={`inline-flex items-center justify-center w-[120px] px-2 py-0.5 rounded-full text-[10px] font-semibold text-center ${cls}`}>
                            {phase.replace(/_/g, ' ')}
                        </span>
                    </div>
                );
            },
        },
        // A, B — plain columns
        // ...['A', 'B'].map((letter) => {
        //     const dbCol = `col_${letter.toLowerCase()}`;
        //     const dataKey = `col${letter}`;
        //     const colIdx = EDITABLE_COL_IDS.indexOf(`col${letter}`);
        //     return {
        //         id: `col${letter}`,
        //         header: letter,
        //         size: 90,
        //         cell: ({ row }) => {
        //             const r = row.original;
        //             return (
        //                 <EditableCell
        //                     value={r[dataKey]}
        //                     onSave={val => handleCellSave(r.id, dbCol, val)}
        //                     rowIdx={row.index}
        //                     colIdx={colIdx}
        //                 />
        //             );
        //         },
        //     };
        // }),
        // C, D — full blue
        // ...['small_cutting', 'production_finished'].map((letter) => {
        //     const dbCol = `col_${letter.toLowerCase()}`;
        //     const dataKey = `col${letter}`;
        //     const colIdx = EDITABLE_COL_IDS.indexOf(`col${letter}`);
        //     return {
        //         id: `col${letter}`,
        //         header: () => <span className="text-blue-800 font-bold">{letter}</span>,
        //         size: 90,
        //         meta: { colBg: 'bg-blue-100', headerBg: 'bg-blue-200' },
        //         cell: ({ row }) => {
        //             const r = row.original;
        //             return (
        //                 <EditableCell
        //                     value={r[dataKey]}
        //                     onSave={val => handleCellSave(r.id, dbCol, val)}
        //                     hoverBg="hover:bg-blue-200"
        //                     rowIdx={row.index}
        //                     colIdx={colIdx}
        //                 />
        //             );
        //         },
        //     };
        // }),
        {
            id: 'colC',
            header: () => <span className="text-blue-800 font-bold">Small Cutting</span>,
            size: 90,
            meta: { colBg: 'bg-blue-100', headerBg: 'bg-blue-200' },
            cell: ({ row }) => {
                const r = row.original;
                const dbCol = `col_c`;
                const dataKey = `colC`;
                const colIdx = EDITABLE_COL_IDS.indexOf(`colC`);
                return (
                    <EditableCell
                        value={r[dataKey]}
                        onSave={val => handleCellSave(r.id, dbCol, val)}
                        rowIdx={row.index}
                        colIdx={colIdx}
                    />
                );
            },
        },
        {
            id: 'colD',
            header: () => <span className="text-blue-800 font-bold">Production Finished</span>,
            size: 90,
            meta: { colBg: 'bg-blue-100', headerBg: 'bg-blue-200' },
            cell: ({ row }) => {
                const r = row.original;
                const dbCol = `col_d`;
                const dataKey = `colD`;
                const colIdx = EDITABLE_COL_IDS.indexOf(`colD`);
                return (
                    <EditableCell
                        value={r[dataKey]}
                        onSave={val => handleCellSave(r.id, dbCol, val)}
                        rowIdx={row.index}
                        colIdx={colIdx}
                    />
                );
            },
        },
        {
            id: 'colE',
            header: () => <span className="text-green-800 font-bold">C4</span>,
            size: 90,
            meta: { colBg: 'bg-green-100', headerBg: 'bg-green-200' },
            cell: ({ row }) => {
                const r = row.original;
                const dbCol = `col_e`;
                const dataKey = `colE`;
                const colIdx = EDITABLE_COL_IDS.indexOf(`colE`);
                return (
                    <EditableCell
                        value={r[dataKey]}
                        onSave={val => handleCellSave(r.id, dbCol, val)}
                        rowIdx={row.index}
                        colIdx={colIdx}
                    />
                );
            },
        },
        {
            id: 'colF',
            header: () => <span className="text-green-800 font-bold">Production Finished</span>,
            size: 90,
            meta: { colBg: 'bg-green-100', headerBg: 'bg-green-200' },
            cell: ({ row }) => {
                const r = row.original;
                const dbCol = `col_f`;
                const dataKey = `colF`;
                const colIdx = EDITABLE_COL_IDS.indexOf(`colF`);
                return (
                    <EditableCell
                        value={r[dataKey]}
                        onSave={val => handleCellSave(r.id, dbCol, val)}
                        rowIdx={row.index}
                        colIdx={colIdx}
                    />
                );
            },
        },
        // E, F — full green
        // ...['E', 'F'].map((letter) => {
        //     const dbCol = `col_${letter.toLowerCase()}`;
        //     const dataKey = `col${letter}`;
        //     const colIdx = EDITABLE_COL_IDS.indexOf(`col${letter}`);
        //     return {
        //         id: `col${letter}`,
        //         header: () => <span className="text-green-800 font-bold">{letter}</span>,
        //         size: 90,
        //         meta: { colBg: 'bg-green-100', headerBg: 'bg-green-200' },
        //         cell: ({ row }) => {
        //             const r = row.original;
        //             return (
        //                 <EditableCell
        //                     value={r[dataKey]}
        //                     onSave={val => handleCellSave(r.id, dbCol, val)}
        //                     hoverBg="hover:bg-green-200"
        //                     rowIdx={row.index}
        //                     colIdx={colIdx}
        //                 />
        //             );
        //         },
        //     };
        // }),
        {
            id: 'deliveryTime',
            header: 'Delivery Time',
            size: 110,
            cell: ({ row }) => {
                const r = row.original;
                return (
                    <span>
                        {r.deliveryTime}
                    </span>
                );
            },
        },
        {
            id: 'scheduledDate',
            header: 'Date',
            size: 120,
            cell: ({ row }) => {
                const r = row.original;
                return (
                    <DateCell
                        value={r.scheduledDate}
                        onSave={val => handleCellSave(r.id, 'scheduled_date', val)}
                        rowIdx={row.index}
                        colIdx={EDITABLE_COL_IDS.indexOf('scheduledDate')}
                        quoteDate={r.quoteDeliveryDate}
                    />
                );
            },
        },
        {
            id: 'actions',
            header: '',
            size: 40,
            cell: ({ row }) => canEdit ? (
                <button
                    onClick={() => handleRemoveRow(row.original.id)}
                    className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded"
                    title="Remove from schedule"
                >
                    <FiTrash2 className="w-3.5 h-3.5" />
                </button>
            ) : null,
        },
    ], [handleCellSave, handleRemoveRow]);

    const table = useReactTable({
        data: displayRows,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    const { rows: tableRows } = table.getRowModel();

    const rowVirtualizer = useVirtualizer({
        count: tableRows.length,
        getScrollElement: () => tableContainerRef.current,
        estimateSize: () => 48,
        getItemKey: index => tableRows[index].original.id,
        overscan: 8,
    });

    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
    const paddingBottom = virtualRows.length > 0 ? totalSize - virtualRows[virtualRows.length - 1].end : 0;

    // Scroll to bottom once when data first loads
    useEffect(() => {
        if (!hasScrolledToBottomRef.current && tableRows.length > 0) {
            hasScrolledToBottomRef.current = true;
            rowVirtualizer.scrollToIndex(tableRows.length - 1, { align: 'end' });
        }
    }, [tableRows.length, rowVirtualizer]);


    // ── drag-over indicator ────────────────────────────────────────────────────
    const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
    const handleDragLeave = (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false); };
    const handleDrop = (e) => { e.preventDefault(); setIsDragOver(false); onDrop(e); };

    // ── dnd-kit sensors + handlers ────────────────────────────────────────────
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    const handleDragStart = useCallback(({ active }) => {
        isDraggingRef.current = true;
        setActiveRowId(active.id);
    }, []);

    const handleDragEnd = useCallback(({ active, over }) => {
        isDraggingRef.current = false;
        setActiveRowId(null);
        if (!over || active.id === over.id) return;

        setDisplayIds(prev => {
            const oldIndex = prev.indexOf(active.id);
            const newIndex = prev.indexOf(over.id);
            if (oldIndex === -1 || newIndex === -1) return prev;
            const newOrder = arrayMove(prev, oldIndex, newIndex);

            // Keep the lock held until the save actually finishes — releasing it on a
            // fixed timer let a still-in-flight save race the next realtime-triggered
            // resync, which would resort the table from partially-saved data and snap
            // the dragged row back toward its original position.
            reorderLockRef.current = true;
            const releaseLock = () => { reorderLockRef.current = false; };

            const changedUpdates = newOrder
                .map((id, idx) => {
                    const row = rows.find(r => r.id === id);

                    return {
                        id,
                        sort_order: idx,
                        current_sort_order: row?.sortOrder ?? 0,
                    };
                })
                .filter(r => r.sort_order !== r.current_sort_order);

            if (hasActiveTableFilter) {

                const filterKey = getProductionSortKey({
                    quoteTypeFilter,
                    jobTypeFilter,
                    urgentFilter,
                    hideCompleted,
                });




                const variantRows = newOrder
                    .map(id => rows.find(r => r.id === id))
                    .filter(Boolean);


                updateProductionScheduleVariantOrder(
                    company.id,
                    filterKey,
                    variantRows
                ).catch(err => {
                    console.error('Failed to save filtered order:', err);
                    Toast.error('Failed to save order', { position: 'bottom-right' });
                }).finally(releaseLock);


            } else {

                updateProductionScheduleSortOrder(changedUpdates).catch(err => {
                    console.error('Failed to save sort order:', err);
                    Toast.error('Failed to save order', { position: 'bottom-right' });
                }).finally(releaseLock);

            }

            return newOrder;
        });
    }, [
        rows,
        hasActiveTableFilter,
        quoteTypeFilter,
        jobTypeFilter,
        urgentFilter,
        hideCompleted,
        company.id
    ]);

    const handleDragCancel = useCallback(() => {
        isDraggingRef.current = false;
        setActiveRowId(null);
    }, []);

    // ── build assignment shape expected by AssignmentContextMenu ──────────────
    const buildAssignment = useCallback((r) => ({
        // ids
        _id: r.jobId,
        _originalId: r.jobId,
        _productionRowId: r.id,   // job_production.id — used by production phase actions
        id: r.jobId,
        // display
        uniqueId: r.uniqueId,
        unique_id: r.uniqueId,
        // quote context — page=production uses isSeparated path
        _quoteType: r.quoteType,
        quote_info: { id: r.quoteId || null, quote_type: r.quoteType },
        // production record shape expected by getProductionStatus()
        quotes: {
            [r.quoteType]: {
                production: {
                    phase: r.phase || 'not_started',
                    userId: r.userId || null,
                    name: r.userName || null,
                    startCuttingAt: r.startCuttingAt || null,
                    startProductionAt: r.startProductionAt || null,
                },
                isRequired: true,
            },
        },
        // misc
        jobType: r.jobType,
        on_hold: false,
    }), []);

    const ctxMenuHandlers = useMemo(() => ({
        availableDesigners: availableDesigners || [],
        userRole,
        onOpenAssignment,
        onEditAssignment: onOpenAssignment,
        onStartProduction,
        onEndProduction,
        onForceFinish,
        onToggleWaiting: handleToggleWaiting,
        onToggleSentProduction: handleToggleSentProduction,
        onAssignDesigner: () => { },
        onDuplicateJob: () => { },
        onToggleHold: () => { },
        onToggleQuoteHold: () => { },
    }), [availableDesigners, userRole, onOpenAssignment, onStartProduction, onEndProduction, onForceFinish, handleToggleWaiting, handleToggleSentProduction]);

    return (
        <ActiveCellCtx.Provider value={{ activeCell, setActiveCell, totalRows: displayRows.length, canEdit }}>
            <div className="flex flex-col h-full rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">

                {sortBy === 'sort_order' && hasSearch && (
                    <span className="text-[14px] py-3 text-amber-800 bg-amber-300 text-center font-semibold">
                        Clear search to reorder table
                    </span>
                )}
                {/* Filter bar */}
                <FilterBar
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    quoteTypeFilter={quoteTypeFilter}
                    setQuoteTypeFilter={setQuoteTypeFilter}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    jobTypeFilter={jobTypeFilter}
                    setJobTypeFilter={setJobTypeFilter}
                    urgentFilter={urgentFilter}
                    setUrgentFilter={setUrgentFilter}
                    hideCompleted={hideCompleted}
                    setHideCompleted={setHideCompleted}
                    totalRows={displayRows.length}
                />

                <p className="production-scroll-hint hidden">Swipe across to view all production columns</p>
                {/* Table scroll container — 80vh */}
                <div
                    ref={tableContainerRef}
                    className={`production-table-scroll overflow-auto transition-all ${isDragOver ? 'ring-2 ring-indigo-400 ring-inset bg-indigo-50/30' : ''}`}
                    style={{ height: '80vh' }}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center h-40">
                            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                            <span className="ml-3 text-sm text-gray-500">Loading…</span>
                        </div>
                    ) : (
                        <DndContext
                            sensors={isMobile ? [] : sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragCancel={handleDragCancel}
                        >
                            <SortableContext items={displayIds} strategy={verticalListSortingStrategy}>
                                <table className="w-full text-xs border-collapse" style={{ minWidth: 900, '--production-table-width': `${table.getTotalSize() + (canDragSort ? 28 : 0)}px` }}>
                                    <thead className="sticky top-0 z-20 bg-white">
                                        {table.getHeaderGroups().map(hg => (
                                            <tr key={hg.id}>
                                                {canDragSort && (
                                                    <th style={{ width: 28 }} className="px-1 py-2.5 bg-gray-600 border border-gray-400" />
                                                )}
                                                {hg.headers.map(header => {
                                                    const meta = header.column.columnDef.meta || {};
                                                    return (
                                                        <th
                                                            key={header.id}
                                                            style={{ width: header.column.getSize() }}
                                                            className={`px-3 py-2.5 text-left text-[11px] font-semibold text-gray-100 border whitespace-nowrap ${meta.headerBg || 'bg-gray-600'}`}
                                                        >
                                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                                        </th>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </thead>

                                    <tbody>
                                        {paddingTop > 0 && <tr><td colSpan={columns.length + (canDragSort ? 1 : 0)} style={{ height: paddingTop }} /></tr>}

                                        {virtualRows.length === 0 && !isLoading && (
                                            <tr>
                                                <td colSpan={columns.length + (canDragSort ? 1 : 0)}>
                                                    <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                                                        {isDragOver ? (
                                                            <p className="text-indigo-500 font-medium">Drop here to add to schedule</p>
                                                        ) : (
                                                            <>
                                                                <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                                                    <FiFilter className="w-5 h-5 text-gray-300" />
                                                                </div>
                                                                <p className="text-sm font-medium text-gray-500">No rows in production schedule</p>
                                                                <p className="text-xs text-gray-400 mt-1">Drag job cards from below to add them here</p>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}

                                        {virtualRows.map(vRow => {
                                            const row = tableRows[vRow.index];
                                            const r = row.original;
                                            const assignment = buildAssignment(r);
                                            return (
                                                <SortableRow
                                                    key={r.id}
                                                    rowId={r.id}
                                                    virtualIndex={vRow.index}
                                                    measureElement={rowVirtualizer.measureElement}
                                                    row={row}
                                                    r={r}
                                                    canDragSort={canDragSort}
                                                    assignment={assignment}
                                                    ctxMenuProps={ctxMenuHandlers}
                                                />
                                            );
                                        })}

                                        {paddingBottom > 0 && <tr><td colSpan={columns.length + (canDragSort ? 1 : 0)} style={{ height: paddingBottom }} /></tr>}
                                    </tbody>
                                </table>
                            </SortableContext>

                            <DragOverlay>
                                {activeRowId && (() => {
                                    const r = displayRows.find(row => row.id === activeRowId);
                                    return r ? (
                                        <table style={{ width: '100%', minWidth: 900 }}>
                                            <tbody>
                                                <tr className="bg-white shadow-xl border-2 border-indigo-400" style={{ height: 48 }}>
                                                    <td style={{ width: 28 }} className="px-1 text-indigo-400">
                                                        <GripVertical className="w-3.5 h-3.5 mx-auto" />
                                                    </td>
                                                    <td className="px-2 py-1 text-xs font-semibold text-gray-900" style={{ width: 170 }}>
                                                        {r.childUniqueId || r.uniqueId}
                                                    </td>
                                                    <td className="px-2 py-1 text-xs text-gray-700" style={{ width: 140 }}>
                                                        {r.customerName}
                                                    </td>
                                                    <td className="px-2 py-1 text-xs text-gray-400">
                                                        {r.quoteType}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    ) : null;
                                })()}
                            </DragOverlay>
                        </DndContext>
                    )}

                    {/* Drop zone overlay when dragging */}
                    {isDragOver && (
                        <div className="sticky bottom-0 left-0 right-0 flex items-center justify-center py-3 bg-indigo-500/10 border-t-2 border-dashed border-indigo-400">
                            <p className="text-indigo-600 text-sm font-semibold">Drop to add to production schedule</p>
                        </div>
                    )}
                </div>
            </div>
        </ActiveCellCtx.Provider>
    );
};

export default ProductionTable;
